import { ColumnDef, CellContext } from "@tanstack/react-table";
import { ReactNode } from "react";
import { SortFieldType } from "./sortUtils";
import Icon from "@/components/Icon";

interface BuildTwoLineColumnProps<T> {
  accessorKey: string;
  header: string;
  getPrimaryText: (row: T) => string | ReactNode;
  getSecondaryText: (row: T) => string | ReactNode;
  secondaryLabel?: string;
  size?: number;
  minSize?: number;
  isFirstColumn?: boolean;
  sortType?: SortFieldType;
  showNavigateIcon?: boolean | ((row: T) => boolean);
  left?: (row: T) => ReactNode;
}

/**
 * Builds a two-line column with consistent styling (no avatar)
 * Automatically detects mobile-tablet/desktop using useIsCompactLayout hook
 * Mobile: Line 1 uses mb-1 and text-body-medium-semibold (default) or text-body-small (if isFirstColumn: false)
 * Desktop: Line 1 uses text-body-mediume without mb-1 or semibold
 * Line 2: text-body-small text-sys-brand-on-surface-variant (with optional label prefix on mobile)
 *
 * @example
 * ```tsx
 * // First column - semibold and prominent on mobile (default)
 * buildTwoLineColumn({
 *   accessorKey: "activity",
 *   header: "PAT Activity",
 *   getPrimaryText: (row) => row.eventName || "Standard PAT",
 *   getSecondaryText: (row) => row.date,
 *   secondaryLabel: "Date", // Shows "Date: March 15, 2024" on mobile
 *   sortType: "text", // Auto-enables sorting (desktop + mobile)
 *   size: 300
 * })
 *
 * // With conditional navigate icon (for clickable rows)
 * buildTwoLineColumn({
 *   accessorKey: "activity",
 *   header: "PAT Activity",
 *   getPrimaryText: (row) => row.eventName || "Standard PAT",
 *   getSecondaryText: (row) => row.date,
 *   showNavigateIcon: (row) => row.slug && row.type === "Standard PAT", // Conditional
 *   size: 300
 * })
 *
 * // Secondary column - regular weight (opt-out)
 * buildTwoLineColumn({
 *   accessorKey: "program",
 *   header: "Program",
 *   getPrimaryText: (row) => row.programName,
 *   getSecondaryText: (row) => row.level,
 *   isFirstColumn: false,
 *   size: 200
 * })
 * // Note: sortType presence automatically enables sorting (desktop + mobile)
 * ```
 */
export function buildTwoLineColumn<T>({
  accessorKey,
  header,
  getPrimaryText,
  getSecondaryText,
  secondaryLabel,
  size = 200,
  minSize,
  isFirstColumn = true,
  sortType,
  showNavigateIcon = false,
  left
}: BuildTwoLineColumnProps<T>): ColumnDef<T> {
  const CellComponent = (props: CellContext<T, unknown>) => {
    const { row, table } = props;
    const isCompactLayout = table.options.meta?.isCompactLayout;
    const primaryTextClass = isCompactLayout
      ? `mb-1 ${isFirstColumn ? "text-body-medium-semibold" : "text-body-small"}`
      : "text-body-medium";

    const primaryText = getPrimaryText(row.original);
    const secondaryText = getSecondaryText(row.original);

    const shouldShowIcon = typeof showNavigateIcon === "function" ? showNavigateIcon(row.original) : showNavigateIcon;

    return (
      <div className="flex items-center gap-2">
        {left && left(row.original)}
        <div className="flex flex-col">
          <div className={`flex items-center gap-1 ${primaryTextClass}`}>
            <span>{primaryText || <span className="opacity-20">--</span>}</span>
            {shouldShowIcon && <Icon name="navigate_next" className="text-sys-brand-on-surface-variant" />}
          </div>
          <div
            className={
              isCompactLayout
                ? "text-body-small text-sys-brand-on-surface-variant whitespace-normal break-words"
                : "flex items-center gap-2 text-body-small text-sys-brand-on-surface-variant"
            }
          >
            {secondaryLabel && (
              <span className={isCompactLayout ? "mr-1 whitespace-nowrap" : undefined}>{secondaryLabel}:</span>
            )}
            <span className={isCompactLayout ? "whitespace-normal break-words" : undefined}>
              {secondaryText || <span className="opacity-20">--</span>}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return {
    accessorKey,
    header,
    size,
    minSize: minSize ?? size,
    meta: {
      headerClassName: "hide-header-in-column",
      ...(sortType && { sortType })
    },
    cell: CellComponent
  };
}
